import { DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, DestroyRef, inject, model, signal } from '@angular/core';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NzDividerComponent } from 'ng-zorro-antd/divider';

import { AuthService, NavigationService, ViewService } from 'core';
import { KorviuLogoComponent } from 'shared';
import { User } from 'shared/types/user.model';
import { UserProfileComponent } from '../user-profile/user-profile.component';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';

type LeftSidebarSectionType = 'general' | 'special';
type LeftSidebarPermission = 'us-member';

interface LeftSidebarSection {
  readonly id: string;
  readonly default: string;
  readonly active: string;
  readonly url: string;
  readonly label: string;
  readonly type: LeftSidebarSectionType;
  readonly permission?: LeftSidebarPermission;
}

const LEFT_SIDEBAR_SECTIONS: readonly LeftSidebarSection[] = [
  {
    id: 'dashboard',
    default: 'assets/img/left-sidebar/Dashboard - Default.svg',
    active: 'assets/img/left-sidebar/Dashboard - Selected.svg',
    url: 'dashboard/home',
    label: 'leftSidebar.dashboard',
    type: 'general'
  },
  {
    id: 'my-referrals',
    default: 'assets/img/left-sidebar/My Referrals - Default.svg',
    active: 'assets/img/left-sidebar/My Referrals - Selected.svg',
    url: 'my-referrals',
    label: 'leftSidebar.buildYourNetwork',
    type: 'general'
  },
  {
    id: 'my-forest',
    default: 'assets/img/left-sidebar/My Forest - Default.svg',
    active: 'assets/img/left-sidebar/My Forest - Selected.svg',
    url: 'my-forest',
    label: 'leftSidebar.eligibilityStatus',
    type: 'general'
  },
  {
    id: 'promotions',
    default: 'assets/img/left-sidebar/Promotions - Default.svg',
    active: 'assets/img/left-sidebar/Promotions - Selected.svg',
    url: 'promotions',
    label: 'leftSidebar.promotions',
    type: 'general'
  },
  {
    id: 'rewards',
    default: 'assets/img/left-sidebar/Rewards - Default.svg',
    active: 'assets/img/left-sidebar/Rewards - Selected.svg',
    url: 'rewards',
    label: 'leftSidebar.rewards',
    type: 'general'
  },
  {
    id: 'balance',
    default: 'assets/img/left-sidebar/Account Balance - Default.svg',
    active: 'assets/img/left-sidebar/Account Balance - Selected.svg',
    url: 'balance',
    label: 'leftSidebar.accountBalance',
    type: 'general'
  },
  {
    id: 'my-okeey-cash',
    default: 'assets/img/left-sidebar/My Okeey Cash - Default.svg',
    active: 'assets/img/left-sidebar/My Okeey Cash - Selected.svg',
    url: 'my-okeey-cash',
    label: 'leftSidebar.myOkeeyCash',
    type: 'general',
    permission: 'us-member'
  },
  /*{
    default: '',
    active: '',
    url: 'ganamigos-network',
    label: 'leftSidebar.ganamigosNetwork'
  },*/
  {
    id: 'profile',
    default: 'assets/img/left-sidebar/Profile - Default.svg',
    active: 'assets/img/left-sidebar/Profile - Selected.svg',
    url: 'profile',
    label: 'leftSidebar.settings',
    type: 'special'
  },
  {
    id: 'faq',
    default: 'assets/img/left-sidebar/Help&Support - Default.svg',
    active: 'assets/img/left-sidebar/Help&Support - Selected.svg',
    url: 'faq',
    label: 'leftSidebar.helpSupport',
    type: 'special'
  }
];

@Component({
  selector: 'workspace-left-sidebar',
  templateUrl: './left-sidebar.component.html',
  styleUrls: ['./left-sidebar.component.scss'],
  standalone: true,
  hostDirectives: [RefreshOnLanguageChangeDirective],
  imports: [
    NgClass,
    KorviuLogoComponent,
    RouterModule,
    TranslateModule,
    UserProfileComponent,
    NzDividerComponent,
    NgTemplateOutlet,
    CdkDropList,
    CdkDrag
  ]
})
export class LeftSidebarComponent {
  readonly #document = inject(DOCUMENT);
  readonly #destroyRef = inject(DestroyRef);
  readonly #authService = inject(AuthService);
  readonly #router = inject(Router);
  readonly #navigationService = inject(NavigationService);
  readonly #viewService = inject(ViewService);
  readonly #generalSectionOrder = signal(
    LEFT_SIDEBAR_SECTIONS.filter(section => section.type === 'general').map(section => section.id)
  );

  readonly isCollapsed = model.required<boolean>();

  public readonly sections = LEFT_SIDEBAR_SECTIONS;
  public readonly user = this.#authService.currentUser$;
  public readonly dragStartDelay = { touch: 350, mouse: 0 } as const;
  public readonly isNavigationBlocked = signal(false);
  public readonly generalSections = computed<readonly LeftSidebarSection[]>(() => {
    const accessibleSectionsById = new Map(
      this.sections
        .filter(section => section.type === 'general' && this.#isSectionAvailable(section, this.user()))
        .map(section => [section.id, section])
    );

    return this.#generalSectionOrder()
      .map(sectionId => accessibleSectionsById.get(sectionId))
      .filter((section): section is LeftSidebarSection => section !== undefined);
  });
  public readonly specialSections = computed<readonly LeftSidebarSection[]>(() =>
    this.sections.filter(section => section.type === 'special' && this.#isSectionAvailable(section, this.user()))
  );

  private readonly _bodyScrollLockId = 'left-sidebar';
  private _workingAreaClickCleanup?: () => void;
  private _navigationUnblockTimeout?: ReturnType<typeof setTimeout>;

  public ngOnInit(): void {
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.collapse();
      });
  }

  public ngOnDestroy(): void {
    this.#viewService.unlockBodyScroll(this._bodyScrollLockId);
    this._workingAreaClickCleanup?.();
    this._workingAreaClickCleanup = undefined;
    clearTimeout(this._navigationUnblockTimeout);
  }

  public isActiveRoute(route: string): boolean {
    return this.#navigationService.isCurrentPath(route);
  }

  public reorderGeneralSections(event: CdkDragDrop<readonly LeftSidebarSection[]>): void {
    const visibleSections = [...this.generalSections()];
    const isValidIndex =
      event.previousIndex >= 0 &&
      event.previousIndex < visibleSections.length &&
      event.currentIndex >= 0 &&
      event.currentIndex < visibleSections.length;

    if (!isValidIndex || event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(visibleSections, event.previousIndex, event.currentIndex);

    // Persistence stays intentionally deferred until the menu-preferences API contract is available.
    const visibleSectionIds = new Set(visibleSections.map(section => section.id));
    let visibleSectionIndex = 0;
    this.#generalSectionOrder.update(sectionOrder =>
      sectionOrder.map(sectionId =>
        visibleSectionIds.has(sectionId) ? visibleSections[visibleSectionIndex++].id : sectionId
      )
    );
  }

  public onDragStarted(): void {
    clearTimeout(this._navigationUnblockTimeout);
    this.isNavigationBlocked.set(true);
  }

  public onDragEnded(): void {
    clearTimeout(this._navigationUnblockTimeout);
    this._navigationUnblockTimeout = setTimeout(() => {
      this.isNavigationBlocked.set(false);
      this._navigationUnblockTimeout = undefined;
    }, 250);
  }

  public preventNavigationDuringDrag(event: MouseEvent): void {
    if (!this.isNavigationBlocked()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  public collapse(): void {
    this.#viewService.unlockBodyScroll(this._bodyScrollLockId);
    this.isCollapsed.set(true);
    this._workingAreaClickCleanup?.();
    this._workingAreaClickCleanup = undefined;
  }

  public uncollapse(): void {
    this.#viewService.lockBodyScroll(this._bodyScrollLockId);
    this.isCollapsed.set(false);
    this._workingAreaClickCleanup?.();

    const workingArea = this.#document.getElementById('working-area');
    if (!workingArea) {
      return;
    }

    const onWorkingAreaClick = () => {
      this.collapse();
    };

    workingArea.addEventListener('click', onWorkingAreaClick);
    this._workingAreaClickCleanup = () => {
      workingArea.removeEventListener('click', onWorkingAreaClick);
    };
  }

  #isSectionAvailable(section: LeftSidebarSection, user: User | null): boolean {
    return section.permission !== 'us-member' || user?.isUsMember === true;
  }
}
